import { Fragment, useCallback, useEffect, useState } from 'react';
import { api, downloadFile, errorText, formatDate, formatDateTime } from '../api';
import { Badge, Card, Empty, Loading, Notice } from '../components/ui';

interface Abschnitt {
  id: string;
  schluessel: string;
  position: number;
  titel: string;
  text: string;
  hinweis: string;
  geaendert_am: string;
}

interface Fassung {
  id: string;
  version: number;
  gueltig_ab: string;
  filename: string;
  sha256: string;
  offene_abschnitte: number;
  erzeugt_am: string;
  erzeugt_von_name: string | null;
}

export function DocumentationPage() {
  const [abschnitte, setAbschnitte] = useState<Abschnitt[] | null>(null);
  const [fassungen, setFassungen] = useState<Fassung[]>([]);
  const [entwuerfe, setEntwuerfe] = useState<Record<string, string>>({});
  const [offen, setOffen] = useState<string | null>(null);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [sections, versions] = await Promise.all([
        api<Abschnitt[]>('/api/verfahrensdoku/sections'),
        api<Fassung[]>('/api/verfahrensdoku/versions'),
      ]);
      setAbschnitte(sections);
      setFassungen(versions);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function speichern(abschnitt: Abschnitt) {
    const text = entwuerfe[abschnitt.id];
    if (text === undefined) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/verfahrensdoku/sections/${abschnitt.id}`, { method: 'PUT', body: { text } });
      setMessage(`"${abschnitt.titel}" gespeichert.`);
      setEntwuerfe({ ...entwuerfe, [abschnitt.id]: undefined as unknown as string });
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function erzeugen() {
    setBusy(true);
    setError('');
    try {
      await downloadFile('/api/verfahrensdoku/generate', { method: 'POST' });
      setMessage('Fassung erzeugt, heruntergeladen und unveränderlich im Belegarchiv abgelegt.');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!abschnitte) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  const offeneAnzahl = abschnitte.filter((abschnitt) => abschnitt.text.trim().length === 0).length;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Card
        title="Verfahrensdokumentation"
        actions={
          <button className="primary small" onClick={erzeugen} disabled={busy}>
            {busy ? 'Erzeuge …' : 'Fassung erzeugen'}
          </button>
        }
      >
        <div className="small muted">
          Die Dokumentation besteht aus zwei Hälften. Der <strong>technische Teil</strong> —
          Datenmodell, Unveränderbarkeits-Trigger, Nummernkreise, Schnittstellen, Zeitpläne,
          Aufbewahrungsfristen, Änderungsstand und der Nachweis der Prüfläufe — wird bei jeder
          Erzeugung frisch aus dem laufenden System ausgelesen und kann deshalb nicht veralten.
          <br />
          <br />
          Der <strong>beschreibende Teil</strong> steht unten und kann nur von Hand geschrieben
          werden: wer welche Aufgabe hat, wie ein Beleg in den Prozess kommt, wer freigibt, wer
          vertritt. Genau danach fragt ein Prüfer.
        </div>

        <div style={{ marginTop: 12 }}>
          {offeneAnzahl === 0 ? (
            <Badge kind="ok">alle {abschnitte.length} Abschnitte ausgefüllt</Badge>
          ) : (
            <Badge kind="warn">
              {offeneAnzahl} von {abschnitte.length} Abschnitten noch offen
            </Badge>
          )}
          <div className="small muted" style={{ marginTop: 8 }}>
            Offene Abschnitte werden im Dokument als offen ausgewiesen, nicht verschwiegen. Eine
            Fassung mit sichtbaren Lücken ist ehrlicher als eine, die etwas über den Betrieb
            behauptet, was nicht stimmt.
          </div>
        </div>
      </Card>

      <Card title="Beschreibender Teil" padded={false}>
        <table>
          <thead>
            <tr>
              <th>Abschnitt</th>
              <th>Stand</th>
              <th>Zuletzt geändert</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {abschnitte.map((abschnitt) => (
              // Fragment mit Schluessel: ohne ihn ordnet React die Zeilen beim
              // naechsten Laden falsch zu, und der geoeffnete Editor springt
              // auf einen anderen Abschnitt.
              <Fragment key={abschnitt.id}>
                <tr>
                  <td>{abschnitt.titel}</td>
                  <td>
                    {abschnitt.text.trim() ? (
                      <Badge kind="ok">{abschnitt.text.trim().length} Zeichen</Badge>
                    ) : (
                      <Badge kind="warn">offen</Badge>
                    )}
                  </td>
                  <td className="small muted">
                    {abschnitt.text.trim() ? formatDateTime(abschnitt.geaendert_am) : '—'}
                  </td>
                  <td className="num">
                    <button
                      className="small ghost"
                      onClick={() => setOffen(offen === abschnitt.id ? null : abschnitt.id)}
                    >
                      {offen === abschnitt.id ? 'schliessen' : 'bearbeiten'}
                    </button>
                  </td>
                </tr>
                {offen === abschnitt.id && (
                  <tr>
                    <td colSpan={4}>
                      <div className="small muted" style={{ marginBottom: 8 }}>
                        <strong>Hineingehört:</strong> {abschnitt.hinweis}
                      </div>
                      <textarea
                        style={{ width: '100%', minHeight: 200, fontFamily: 'inherit' }}
                        value={entwuerfe[abschnitt.id] ?? abschnitt.text}
                        onChange={(e) =>
                          setEntwuerfe({ ...entwuerfe, [abschnitt.id]: e.target.value })
                        }
                      />
                      <div style={{ marginTop: 8 }}>
                        <button
                          className="primary small"
                          onClick={() => speichern(abschnitt)}
                          disabled={busy}
                        >
                          speichern
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="Fassungen" padded={false}>
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Für jeden geprüften Zeitraum muss feststellbar sein, welche Fassung damals galt. Jede
          Erzeugung bekommt deshalb eine Nummer und wandert unverändert ins Belegarchiv.
        </div>
        {fassungen.length === 0 ? (
          <Empty>Noch keine Fassung erzeugt.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Fassung</th>
                <th>Gültig ab</th>
                <th>Erzeugt</th>
                <th>Offene Abschnitte</th>
                <th>Prüfsumme</th>
                <th>Durch</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {fassungen.map((fassung) => (
                <tr key={fassung.id}>
                  <td className="mono">{fassung.version}</td>
                  <td className="small">{formatDate(fassung.gueltig_ab)}</td>
                  <td className="small muted">{formatDateTime(fassung.erzeugt_am)}</td>
                  <td>
                    {fassung.offene_abschnitte === 0 ? (
                      <Badge kind="ok">vollständig</Badge>
                    ) : (
                      <Badge kind="warn">{fassung.offene_abschnitte} offen</Badge>
                    )}
                  </td>
                  <td className="small mono" title={fassung.sha256}>
                    {fassung.sha256.slice(0, 12)}…
                  </td>
                  <td className="small">{fassung.erzeugt_von_name ?? '—'}</td>
                  <td className="num">
                    <button
                      className="small ghost"
                      onClick={() => downloadFile(`/api/verfahrensdoku/versions/${fassung.id}`)}
                    >
                      laden
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
